import axiosInstance from './axiosInstance';
import {
  CommonEntitiesListResponse,
  HealthMetricCreateRequest,
  HealthMetricUpdateRequest,
  Specialization,
  UnitOfMeasurementCreateRequest,
} from '@docbay/schemas';

/**
 * API methods for fetching, creating, editing, and deleting health metrics.
 */
export const MetricsAPI = {
  /**
   * Fetches a list of health metrics with pagination.
   *
   * @param params - An object containing the page number, limit per page, and optional specialization IDs to filter by.
   * @returns A promise that resolves to a CommonEntitiesListResponse object containing the fetched health metrics.
   */
  fetchMetrics: (params: {
    page: number;
    limit: number;
    specializationIds?: string;
  }) =>
    axiosInstance.get<CommonEntitiesListResponse<any>>('/health-metrics', {
      params,
    }),

  /**
   * Fetches a list of specializations.
   *
   * @returns A promise that resolves to an array of Specialization objects containing the fetched specializations.
   */
  fetchSpecializations: () =>
    axiosInstance.get<Specialization[]>('/specializations'),

  /**
   * Fetches a list of units of measurement for health metrics with pagination.
   *
   * @returns A promise that resolves to a CommonEntitiesListResponse object containing the fetched units of measurement.
   */
  fetchUnitsOfMeasurement: () =>
    axiosInstance.get<CommonEntitiesListResponse<any>>(
      '/health-metrics/unit-of-measurements',
    ),

  /**
   * Creates a new unit of measurement for health metrics.
   *
   * @param data - An object containing the necessary information to create a new unit of measurement.
   * @returns A promise that resolves to the created UnitOfMeasurement object.
   */
  createUnitOfMeasurement: (data: UnitOfMeasurementCreateRequest) =>
    axiosInstance.post('/health-metrics/unit-of-measurements', data),

  /**
   * Deletes an existing unit of measurement for health metrics.
   *
   * @param id - The ID of the unit of measurement to delete.
   * @returns A promise that resolves when the deletion is complete.
   */
  deleteUnitOfMeasurement: (id: string) =>
    axiosInstance.delete(`/health-metrics/unit-of-measurements/${id}`),

  /**
   * Creates a new health metric.
   *
   * @param data - An object containing the necessary information to create a new health metric.
   * @returns A promise that resolves to the created HealthMetric object.
   */
  createMetric: (data: HealthMetricCreateRequest) =>
    axiosInstance.post('/health-metrics', data),

  /**
   * Deletes an existing health metric.
   *
   * @param id - The ID of the health metric to delete.
   * @returns A promise that resolves when the deletion is complete.
   */
  deleteMetric: (id: string) => axiosInstance.delete(`/health-metrics/${id}`),

  /**
   * Edits an existing health metric.
   *
   * @param id - The ID of the health metric to edit.
   * @param data - An object containing the updated information for the health metric.
   * @returns A promise that resolves when the editing is complete.
   */
  editMetric: (id: string, data: HealthMetricUpdateRequest) =>
    axiosInstance.patch(`/health-metrics/${id}`, data),
};
